import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { mockDataService } from '../services/mockDataService';
import { WordPressPost } from '../services/wordpressService';
import AuthenticatedImage from './AuthenticatedImage';

/**
 * ArticleDetailPage Component
 * Displays individual article details with related articles
 */

const ArticleDetailPageContainer = styled.div.attrs({className: 'page-container'})`
  background: #F9FAFA;
  min-height: 100vh;
  
  padding: 0 20px 120px;
  margin-right:auto;
  margin-left:auto;

  @media (min-width: 769px) {
    width:95%;
    padding:0 0 60px;
  }
`;

const BreadcrumbSection = styled.div.attrs({className: 'breadcrumb-section'})`
  padding-top: 80px;
`;

const BreadcrumbContainer = styled.div.attrs({className: 'breadcrumb-container'})`
  max-width: var(--contents-maxwidth-pc);
  margin: 0 auto;
  
`;

const BreadcrumbText = styled.p.attrs({className: 'breadcrumb-text'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  color: #9B9B9B;
  margin: 0;

  a {
    color: #9B9B9B;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #0F3F8E;
    }
  }
`;


const ArticleContentSection = styled.section.attrs({className: 'article-content-section'})`
  max-width: 1280px;
  margin: 0 auto;
  padding-top: 80px;
`;

const ArticleHeader = styled.div.attrs({className: 'article-header'})`
  margin-bottom: 40px;
`;

const ArticleMeta = styled.div.attrs({className: 'article-meta'})`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 10px;
  }
`;

const ArticleDate = styled.span.attrs({className: 'article-date'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.45em;
  color: #9B9B9B;
`;

const ArticleCategory = styled.span.attrs({className: 'article-category'})<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px 1px;
  height: 20px;
  background: ${props => props.$color};
  border-radius: 10px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38em;
  letter-spacing: 0.05em;
  color: #FFFFFF;
  text-wrap-mode: nowrap;
`;

const ArticleTitle = styled.h1.attrs({className: 'article-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 24px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;
`;

const ArticleImage = styled.div.attrs({className: 'article-image'})`
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16/9;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    border-radius: 10px;
  }
`;

const ArticleBody = styled.div.attrs({className: 'article-body'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.8;
  color: #333333;
  margin-bottom: 80px;

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    margin: 30px 0 15px 0;
  }

  p {
    margin: 0 0 20px 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 10px;
    margin: 20px 0;
  }

  a {
    color: #0F3F8E;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RelatedArticlesSection = styled.section.attrs({className: 'related-articles-section'})`
  max-width: 1280px;
  margin: 0 auto;
  padding: 80px;
  background: #FFFFFF;
  border-radius: 20px;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 0;
    padding :0;
  }
`;

const RelatedArticlesTitle = styled.h2.attrs({className: 'related-articles-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 36px;
  line-height: 1.5;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0 0 40px 0;
  text-wrap-mode: nowrap;

  @media (max-width: 768px) {
    grid-column: 1/-1;
    margin: 40px 20px 38px;
  }
`;

const RelatedArticlesGrid = styled.div.attrs({className: 'related-articles-grid'})`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 34px;

  @media (max-width: 768px) {
    // grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const RelatedArticleItem = styled(Link).attrs({className: 'related-article-item'})`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const RelatedArticleImage = styled.div.attrs({className: 'related-article-image'})`
  aspect-ratio: 4/3;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    border-radius: 10px;
  }
`;

const RelatedArticleContent = styled.div.attrs({className: 'related-article-content'})`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RelatedArticleMeta = styled.div.attrs({className: 'related-article-meta'})`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: 768px) {
    align-items: flex-start;
    gap: 10px;
  }
`;

const RelatedArticleDate = styled.span.attrs({className: 'related-article-date'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.45em;
  color: #9B9B9B;
`;

const RelatedArticleCategory = styled.span.attrs({className: 'related-article-category'})<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px 1px;
  height: 20px;
  background: ${props => props.$color};
  border-radius: 10px;
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 13px;
  line-height: 1.38em;
  letter-spacing: 0.05em;
  color: #FFFFFF;
  text-wrap-mode: nowrap;
`;

const RelatedArticleTitle = styled.h3.attrs({className: 'related-article-title'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.75em;
  letter-spacing: 0.05em;
  color: #333333;
  margin: 0;
`;

const RelatedArticleButton = styled.div.attrs({className: 'related-article-button'})`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  border-radius: 26px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  align-self: flex-end;
  margin-top: auto;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-2px);
  }
  
  img {
    width: 20px;
    height: 20px;
  }
`;

const LoadingMessage = styled.div.attrs({className: 'loading-message'})`
  text-align: center;
  padding: 40px;
  font-family: 'Noto Sans JP', sans-serif;
  font-size: 16px;
  color: #9B9B9B;
`;

const ErrorMessage = styled.div.attrs({className: 'error-message'})`
  text-align: center;
  padding: 40px;
  font-family: 'Noto Sans JP', sans-serif;
  font-size: 16px;
  color: #FF6B6B;
`;

const ArticleDetailPage: React.FC = () => {
  const { id: articleId } = useParams<{ id: string }>();
  const [article, setArticle] = useState<WordPressPost | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top when component mounts or articleId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  // Fetch article data
  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Determine which service to use
        const isDevelopment = process.env.NODE_ENV === 'development';
        const useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true' || 
                           (isDevelopment && !process.env.REACT_APP_WORDPRESS_URL);
        
        const service = useMockData ? mockDataService : (await import('../services/wordpressService')).wordpressService;
        
        const fetchedArticle = await service.getPost(parseInt(articleId));
        setArticle(fetchedArticle);
        
        // Fetch related articles (same category, excluding current article)
        if (fetchedArticle && fetchedArticle.categories && fetchedArticle.categories.length > 0) {
          const related = await service.getPosts({
            categories: fetchedArticle.categories,
            per_page: 4,
            orderby: 'date',
            order: 'desc'
          });
          
          // Filter out the current article
          const filteredRelated = related.filter(post => post.id !== fetchedArticle.id);
          setRelatedArticles(filteredRelated.slice(0, 3));
        }
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch article');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [articleId]);

  // Get category color
  const getCategoryColor = (categoryName: string) => {
    switch (categoryName.toLowerCase()) {
      case 'お知らせ':
      case 'news':
        return '#FDC22D';
      case 'リリース':
      case 'release':
        return '#92D382';
      case 'ナレッジ':
      case 'knowledge':
        return '#FF9294';
      default:
        return '#FDC22D';
    }
  };

  if (loading) {
    return (
      <ArticleDetailPageContainer>
        <LoadingMessage>読み込み中...</LoadingMessage>
      </ArticleDetailPageContainer>
    );
  }

  if (error || !article) {
    return (
      <ArticleDetailPageContainer>
        <ErrorMessage>エラー: {error || '記事が見つかりません'}</ErrorMessage>
      </ArticleDetailPageContainer>
    );
  }

  const categoryName = article._embedded?.['wp:term']?.[0]?.[0]?.name || '';

  return (
    <ArticleDetailPageContainer>
      {/* Breadcrumb Navigation */}
      <BreadcrumbSection>
        <BreadcrumbContainer>
          <BreadcrumbText>
            <Link to="/">トップ</Link> ー <Link to="/information">お知らせ</Link> ー {article.title.rendered}
          </BreadcrumbText>
        </BreadcrumbContainer>
      </BreadcrumbSection>


      {/* Article Content Section */}
      <ArticleContentSection>
        <ArticleHeader>
          <ArticleMeta>
            <ArticleDate>
              {new Date(article.date).toLocaleDateString('ja-JP', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              }).replace(/\//g, '.')}
            </ArticleDate>
            {categoryName && (
              <ArticleCategory $color={getCategoryColor(categoryName)}>
                {categoryName}
              </ArticleCategory>
            )}
          </ArticleMeta>
          <ArticleTitle dangerouslySetInnerHTML={{ __html: article.title.rendered }} />
        </ArticleHeader>

        {/* Featured Image */}
        {article._embedded?.['wp:featuredmedia']?.[0]?.source_url && (
          <ArticleImage>
            <AuthenticatedImage 
              src={article._embedded['wp:featuredmedia'][0].source_url} 
              alt={article._embedded['wp:featuredmedia'][0].alt_text || article.title.rendered}
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
            />
          </ArticleImage>
        )}

        {/* Article Content */}
        <ArticleBody dangerouslySetInnerHTML={{ __html: article.content.rendered }} />
      </ArticleContentSection>

      {/* Related Articles Section */}
      {relatedArticles.length > 0 && (
        <RelatedArticlesSection>
          <RelatedArticlesGrid>
            <RelatedArticlesTitle>関連記事</RelatedArticlesTitle>
            {relatedArticles.map((relatedArticle) => (
              <RelatedArticleItem key={relatedArticle.id} to={`/information/${relatedArticle.id}`}>
                <RelatedArticleImage>
                  {relatedArticle._embedded?.['wp:featuredmedia']?.[0]?.source_url ? (
                    <AuthenticatedImage 
                      src={relatedArticle._embedded['wp:featuredmedia'][0].source_url} 
                      alt={relatedArticle._embedded['wp:featuredmedia'][0].alt_text || relatedArticle.title.rendered}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                    />
                  ) : (
                    <div style={{ 
                      width: '100%', 
                      height: '100%', 
                      background: '#D9D9D9', 
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666'
                    }}>
                      画像なし
                    </div>
                  )}
                </RelatedArticleImage>
                <RelatedArticleContent>
                  <RelatedArticleMeta>
                    <RelatedArticleDate>
                      {new Date(relatedArticle.date).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      }).replace(/\//g, '.')}
                    </RelatedArticleDate>
                    {relatedArticle._embedded?.['wp:term']?.[0]?.[0] && (
                      <RelatedArticleCategory $color={getCategoryColor(relatedArticle._embedded['wp:term'][0][0].name)}>
                        {relatedArticle._embedded['wp:term'][0][0].name}
                      </RelatedArticleCategory>
                    )}
                  </RelatedArticleMeta>
                  <RelatedArticleTitle 
                    dangerouslySetInnerHTML={{ __html: relatedArticle.title.rendered }}
                  />
                </RelatedArticleContent>
                {/* <RelatedArticleButton to={`/information/${relatedArticle.id}`}> */}
                <RelatedArticleButton>
                  <img src="/images/arrow-right.svg" alt="arrow right" />
                </RelatedArticleButton>
              </RelatedArticleItem>
            ))}
          </RelatedArticlesGrid>
        </RelatedArticlesSection>
      )}
    </ArticleDetailPageContainer>
  );
};

export default ArticleDetailPage;
